import { SelectProps, Typography } from 'antd';

import { useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import {
  useInsurerMutation,
  useInsurers,
} from '@/core/domain/people/Insurers/Insurers.hooks';
import {
  Insurer,
  ListInsurersQuery,
} from '@/core/domain/people/Insurers/Insurers.types';
import { applyMask } from '@/utils/mask';
import { cnpjMask } from '@/utils/mask/commonMasks';
import useSelectOptions from '@/hooks/useSelectOptions';
import { FaBan } from 'react-icons/fa';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ListInsurersQuery;
  showCnpj?: boolean;
  queryEnable?: boolean;
  onInsurerSelected?: (insurer: Insurer) => void;
};

const InsurerInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  showCnpj = false,
  queryEnable = false,
  ...props
}) => {
  const [selectClicked, setSelectClicked] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const { Text } = Typography;

  const insurerList = useInsurers(
    {
      rows: 30,
      active: true,
      search,
      ...queryFilter,
    },
    (queryEnable || selectClicked) && !props.disabled,
  );

  const insurerDetail = useInsurerMutation();

  const options = useSelectOptions({
    list: insurerList,
    detail: insurerDetail,
    selectProps: props,
    labelFormatter: (insurer) => (
      <>
        <div key={insurer.id}>
          {insurer.active === false && (
            <FaBan size={10} style={{ marginRight: 4 }} />
          )}
          <Text>{insurer.name || ''}</Text>
        </div>

        {showCnpj && (
          <div>
            <Text type="secondary">{applyMask(insurer.cnpj, cnpjMask)}</Text>
          </div>
        )}
      </>
    ),
  });

  return (
    <InfiniteSelect
      fetchNextPage={insurerList.fetchNextPage}
      hasNextPage={insurerList.hasNextPage}
      loading={insurerList.isLoading}
      options={options}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
    />
  );
};

export default InsurerInfiniteSelectField;
